import { Node, mergeAttributes } from '@tiptap/core';

/**
 * ResizableImage — TipTap Node for inline resizable images.
 *
 * Canonical persistence shape (2026-07-08 refactor):
 *   <img data-key="orgId/clinical-docs/uuid.png" alt="filename.png" style="width: 293px">
 *
 * Notably absent: `src`. The persisted HTML never carries a signed
 * URL — signed URLs are ephemeral and belong in memory only. The
 * extension reads `key` on parse and calls a RESOLVER function
 * (configured via the parent editor) to look up the URL for each
 * key at render time. The resolver is backed by:
 *   1. `imageUrls` map from the backend GET response (fresh signed
 *      URLs, per-request, minted from `data-key`)
 *   2. Local blob-URL map for freshly uploaded images (before save)
 *
 * This is the Notion / Slack / Jira pattern for embedded media:
 * document persists a stable reference, URLs resolve on read.
 * See docs/modules/file-service.md → "Rich-text image architecture".
 */
const ResizableImage = Node.create({
    name: 'image',
    group: 'inline',
    inline: true,
    draggable: true,
    atom: true,

    addOptions() {
        return {
            /**
             * `resolveUrl(key: string) => string | null` — returns the
             * URL for a given data-key, or null/undefined if unknown
             * (in which case the img renders as a broken placeholder,
             * which visually signals a missing/expired reference).
             * Configured per-editor via `.configure({ resolveUrl })`.
             */
            resolveUrl: () => null,
        };
    },

    addAttributes() {
        return {
            alt: {
                default: null,
                parseHTML: (el) => el.getAttribute('alt') || null,
                renderHTML: (attrs) => (attrs.alt ? { alt: attrs.alt } : {}),
            },
            title: {
                default: null,
                parseHTML: (el) => el.getAttribute('title') || null,
                renderHTML: (attrs) => (attrs.title ? { title: attrs.title } : {}),
            },
            // Canonical S3 key. THIS is the durable identity of an
            // embedded image — round-trips as `data-key` in the HTML.
            key: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-key'),
                renderHTML: (attrs) => (attrs.key ? { 'data-key': attrs.key } : {}),
            },
            width: {
                default: null,
                parseHTML: (el) => el.getAttribute('width') || el.style.width || null,
            },
            height: {
                default: null,
                parseHTML: (el) => el.getAttribute('height') || el.style.height || null,
            },
        };
    },

    // Accept `<img>` tags with `data-key` (our shape) OR with `src`
    // (a foreign paste from another editor / third-party HTML). If
    // both are present, prefer data-key. If only src is present, we
    // still render the node so the paste-in flow doesn't lose the
    // image, but there's no persistence guarantee — pasted external
    // URLs live and die by the source domain.
    parseHTML() {
        return [{ tag: 'img[data-key]' }, { tag: 'img[src]' }];
    },

    // Serialize back to HTML — deliberately does NOT emit `src`. The
    // stored HTML holds only the reference; `src` is a per-request
    // read-time concern. renderHTML is used both for the editor's
    // getHTML() (what leaves the component on save) and for the
    // node's non-nodeView fallback rendering.
    renderHTML({ HTMLAttributes }) {
        const { width, height, ...rest } = HTMLAttributes;
        const styles = [];
        if (width) {
            const w = typeof width === 'number' ? `${width}px` : width;
            styles.push(`width: ${w}`);
        }
        styles.push('max-width: 100%');
        styles.push('height: auto');
        styles.push('display: inline-block');

        return ['img', mergeAttributes(rest, { style: styles.join('; ') })];
    },

    addCommands() {
        return {
            setImage:
                (options) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    });
                },
        };
    },

    /**
     * Interactive rendering. `img.src` comes from the resolver keyed
     * by `node.attrs.key` — the DOM src is never derived from HTML.
     * The resolver is read on every update so it always sees the
     * latest resolveUrl configured via `.configure({ resolveUrl })`.
     */
    addNodeView() {
        return ({ node, getPos, editor }) => {
            const options = () => editor.extensionManager.extensions.find((e) => e.name === 'image')?.options || {};
            const resolveUrl = (key) => {
                const fn = options().resolveUrl;
                return typeof fn === 'function' && key ? fn(key) : null;
            };

            const wrapper = document.createElement('span');
            wrapper.classList.add('image-resizable-wrapper');
            wrapper.style.display = 'inline-block';
            wrapper.style.position = 'relative';
            wrapper.style.lineHeight = '0';

            const img = document.createElement('img');
            img.src = resolveUrl(node.attrs.key) || '';
            if (node.attrs.alt) img.alt = node.attrs.alt;
            if (node.attrs.title) img.title = node.attrs.title;
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.display = 'inline-block';
            img.style.borderRadius = '4px';
            img.style.cursor = 'pointer';
            if (node.attrs.width) {
                const w = typeof node.attrs.width === 'number' ? `${node.attrs.width}px` : node.attrs.width;
                img.style.width = w;
            }

            img.addEventListener('load', () => {
                requestAnimationFrame(() => {
                    if (editor && !editor.isDestroyed) {
                        editor.view.dispatch(editor.state.tr);
                    }
                });
            });

            wrapper.appendChild(img);

            const createHandle = (direction) => {
                const handle = document.createElement('span');
                handle.classList.add('image-resize-handle', `handle-${direction}`);
                handle.dataset.direction = direction;
                handle.contentEditable = 'false';

                handle.addEventListener('mousedown', (e) => {
                    if (!editor.isEditable) return;
                    e.preventDefault();
                    e.stopPropagation();

                    const startX = e.clientX;
                    const startWidth = img.offsetWidth;

                    const onMouseMove = (ev) => {
                        const diff = direction === 'left' ? startX - ev.clientX : ev.clientX - startX;
                        const newWidth = Math.max(50, startWidth + diff);
                        img.style.width = `${newWidth}px`;
                        wrapper.style.width = `${newWidth}px`;
                    };

                    const onMouseUp = () => {
                        document.removeEventListener('mousemove', onMouseMove);
                        document.removeEventListener('mouseup', onMouseUp);
                        document.body.style.cursor = '';
                        document.body.style.userSelect = '';

                        const finalWidth = img.offsetWidth;
                        const pos = getPos();
                        if (typeof pos === 'number') {
                            editor
                                .chain()
                                .setNodeSelection(pos)
                                .command(({ tr }) => {
                                    tr.setNodeMarkup(pos, undefined, {
                                        ...node.attrs,
                                        width: `${finalWidth}px`,
                                    });
                                    return true;
                                })
                                .run();
                        }
                    };

                    document.body.style.cursor = 'col-resize';
                    document.body.style.userSelect = 'none';
                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                });

                return handle;
            };

            wrapper.appendChild(createHandle('left'));
            wrapper.appendChild(createHandle('right'));

            return {
                dom: wrapper,
                contentDOM: null,
                update: (updatedNode) => {
                    if (updatedNode.type.name !== 'image') return false;
                    img.src = resolveUrl(updatedNode.attrs.key) || '';
                    if (updatedNode.attrs.alt) img.alt = updatedNode.attrs.alt;
                    if (updatedNode.attrs.width) {
                        const w = typeof updatedNode.attrs.width === 'number' ? `${updatedNode.attrs.width}px` : updatedNode.attrs.width;
                        img.style.width = w;
                        wrapper.style.width = w;
                    }
                    return true;
                },
                selectNode: () => {
                    wrapper.classList.add('selected');
                },
                deselectNode: () => {
                    wrapper.classList.remove('selected');
                },
            };
        };
    },
});

export default ResizableImage;
